import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import { ArrowDown, ArrowUp, Undo2, XCircle } from 'lucide-react';
import React from 'react';

/**
 * Centralized status/semantic color lookup — DESIGN-SYSTEM.md §4.
 *
 * Replaces every ad hoc `<Chip color="...">` scattered across list/report
 * screens with a single source of truth for domain -> value -> color.
 * Adoption in each screen happens as that screen's own redesign task runs
 * (see FRONTEND-TASKS.md) — this component is the foundation piece.
 *
 * Usage: <StatusChip domain="sale" value={row.status} />
 *
 * Domains: 'sale' | 'orderService' | 'serviceQuote' | 'stockMovement' |
 *          'checklist' | 'paymentMethod' | 'itemType' | 'connection' |
 *          'personType'
 */

function normalize(value) {
    if (value === undefined || value === null) return '';
    return String(value)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();
}

// tone -> icon shown instead of the default dot, only where the design
// system calls out a specific icon (§4 "Notes" column). Everything else
// uses the default leading dot (pill anatomy, §4).
const STATUS_TABLE = {
    sale: {
        iniciada: { tone: 'info', label: 'Iniciada' },
        finalizada: { tone: 'success', label: 'Finalizada' },
        pago: { tone: 'success', label: 'Pago' },
        paga: { tone: 'success', label: 'Paga' },
        pendente: { tone: 'warning', label: 'Pendente' },
        enviada: { tone: 'info', label: 'Enviada' },
        cancelada: { tone: 'danger', label: 'Cancelada' },
        estornada: { tone: 'warning', label: 'Estornada' },
    },
    orderService: {
        'nao iniciada': { tone: 'warning', label: 'Não Iniciada' },
        'em execucao': { tone: 'info', label: 'Em Execução' },
        finalizada: { tone: 'success', label: 'Finalizada' },
        // API value is literally "Paga" but the design system relabels it
        // "Faturada" in the UI — same color as Finalizada, different copy.
        paga: { tone: 'success', label: 'Faturada' },
        faturada: { tone: 'success', label: 'Faturada' },
        cancelada: { tone: 'danger', label: 'Cancelada' },
        estornada: { tone: 'warning', label: 'Estornada' },
    },
    // Orçamento de Serviço (Service Quote) — 5 status reais retornados pela
    // API (`fv-api/servicequote/status_updater.go`/`converter.go`): "novo",
    // "aguardando aprovacao", "aprovado", "enviado para cliente" e "ordem
    // gerada". A API retorna "ordem gerada", mas o rótulo exibido é sempre
    // "Convertido" (mesma convenção de relabel "Paga" -> "Faturada" de
    // `orderService`, ver DESIGN-SYSTEM.md §4) — tom `purple` (`--purple`),
    // cor nova neste domínio, mesmo `tone` já usado por `itemType.servico`/
    // `paymentMethod.credito`. Introduzido por `pages/service_quotes/List.js`.
    serviceQuote: {
        novo: { tone: 'neutral', label: 'Novo' },
        'enviado para cliente': { tone: 'info', label: 'Enviado' },
        'aguardando aprovacao': { tone: 'warning', label: 'Aguardando Aprovação' },
        aprovado: { tone: 'success', label: 'Aprovado' },
        'ordem gerada': { tone: 'purple', label: 'Ordem Gerada' },
    },
    stockMovement: {
        entrada: { tone: 'success', label: 'Entrada', icon: ArrowUp },
        saida: { tone: 'danger', label: 'Saída', icon: ArrowDown },
        cancelado: { tone: 'neutral', label: 'Cancelado', icon: XCircle },
        'estorno de venda': { tone: 'warning', label: 'Estorno de venda', icon: Undo2 },
    },
    // Nível de estoque de produto (coluna "Estoque" de `pages/products/List.js`,
    // prototipo-lista-produtos.html) — valor computado no cliente a partir da
    // `quantity` da linha, não um valor cru da API. O protótipo tem 3 estados
    // (Sem estoque/Estoque baixo/Em estoque), mas o 3º depende de comparar com
    // `min_quantity`, campo que `GET /stock/all` (`StockItemsResponse`) não
    // expõe hoje (só o endpoint por-produto `/stock/edit/{id}` tem
    // `min_quantity`) — por isso, por ora, só os 2 estados computáveis sem
    // mudança de contrato de API. Ver nota em `pages/products/List.js`.
    stockLevel: {
        'sem estoque': { tone: 'danger', label: 'Sem estoque' },
        'em estoque': { tone: 'success', label: 'Em estoque' },
    },
    checklist: {
        ok: { tone: 'success', label: 'OK' },
        nok: { tone: 'danger', label: 'NOK' },
        manutencao: { tone: 'warning', label: 'Manutenção' },
    },
    paymentMethod: {
        dinheiro: { tone: 'success', label: 'Dinheiro' },
        pix: { tone: 'brand2', label: 'Pix' },
        debito: { tone: 'info', label: 'Débito' },
        credito: { tone: 'purple', label: 'Crédito' },
    },
    itemType: {
        produto: { tone: 'info', label: 'Produto' },
        servico: { tone: 'purple', label: 'Serviço' },
    },
    connection: {
        conectado: { tone: 'success', label: 'Conectado' },
        desconectado: { tone: 'neutral', label: 'Desconectado' },
    },
    // Status do terminal de caixa (não confundir com o status de uma venda) —
    // Aberto/Fechado, mesma cor de `connection.conectado`/`desconectado`.
    // Introduzido por `pages/cashregisters/List.js`/`View.js`/`History.js`
    // (item "Lista de caixas" do FRONTEND-TASKS.md).
    cashRegisterStatus: {
        open: { tone: 'success', label: 'Aberto' },
        aberto: { tone: 'success', label: 'Aberto' },
        closed: { tone: 'neutral', label: 'Fechado' },
        fechado: { tone: 'neutral', label: 'Fechado' },
    },
    // Tipo de pessoa (cliente/fornecedor) — mesma convenção de cor do
    // domínio `itemType` (Produto=info/blue, Serviço=purple), aplicada aqui a
    // PF=info/blue, PJ=purple. Introduzido por `pages/customers/List.js`
    // (Lista de clientes) seguindo `prototipo-lista-fornecedores.html` e
    // `prototipo-detalhe-cliente.html`.
    personType: {
        pf: { tone: 'info', label: 'Pessoa Física' },
        pj: { tone: 'purple', label: 'Pessoa Jurídica' },
    },
    // Tipo de transação financeira (receita/despesa) — mesma convenção de cor
    // positivo=--success/negativo=--danger já usada em Vendas (valor
    // colorido) e no domínio `stockMovement` (Entrada/Saída). Introduzido por
    // `pages/transactions/List.js` (Lista de transações),
    // `prototipo-lista-transacoes.html`.
    transactionType: {
        receita: { tone: 'success', label: 'Receita' },
        despesa: { tone: 'danger', label: 'Despesa' },
    },
    // Status de despesa — só existe para `type === 'despesa'` (receita não
    // tem workflow, ver `pages/transactions/List.js`). "Prestado Contas" é o
    // rótulo literal devolvido pela API (`transaction.ExpenseStatusPrestadoContas`).
    // "Vencido" é um 3º status real (job diário
    // `transaction.MarkOverduePendingExpensesAsVencido` marca despesas
    // pendentes com vencimento passado) que não existe no protótipo
    // (`prototipo-lista-transacoes.html` só modela Pendente/Prestado Contas)
    // — mapeado aqui com tom `danger` (mais urgente que Pendente) em vez de
    // ser escondido/fundido com "Pendente", para não mascarar um dado real
    // da API.
    expenseStatus: {
        pendente: { tone: 'warning', label: 'Pendente' },
        vencido: { tone: 'danger', label: 'Vencido' },
        'prestado contas': { tone: 'success', label: 'Prestado Contas' },
    },
    // Tipo de ação do log de auditoria (`GET /audit/logs`, `AuditLogResponse.action`)
    // — introduzido por `pages/audit/List.js`/`AuditDetailDrawer.js` (Log de
    // auditoria), `prototipo-log-auditoria.html`. Diferente de todo outro
    // domínio desta tabela: a API não devolve um código de status fechado,
    // devolve uma frase livre em português já escrita para leitura humana
    // (ex. "Atualizou um produto", "Cancelou uma venda", "Realizou login no
    // sistema" — ver `fv-api/*/{creator,updater,deleter,canceler}.go`). Por
    // isso o `value` passado para este domínio não é o texto da API, é um
    // "balde" (`criou`/`editou`/`excluiu`/`login`/`outro`) derivado por
    // `classifyAuditAction()` (`pages/audit/List.js`) a partir de prefixos
    // verbais reais (Criou/Gerou = criou; Atualizou/Aplicou/Iniciou/
    // Finalizou/Registrou pagamento/Incluiu estoque = editou; Excluiu/
    // Cancelou/Estornou/Removeu estoque = excluiu) — e o rótulo exibido no
    // pill nunca é o rótulo genérico do balde abaixo, é sempre o texto real
    // da API (`<StatusChip domain="auditAction" value={bucket} label={row.action} />`),
    // para não inventar uma categoria fixa que a API não devolve. As cores
    // reaproveitam convenções já existentes desta mesma tabela onde davam:
    // criar/gerar = `success` (mesmo tom de criação positiva), editar/
    // registrar pagamento/iniciar/finalizar = `warning` (mudança em cima de
    // um registro existente), excluir/cancelar/estornar = `danger` (mesmo
    // tom de `sale.cancelada`/`orderService.cancelada`), login = `neutral`.
    auditAction: {
        criou: { tone: 'success', label: 'Criou' },
        editou: { tone: 'warning', label: 'Editou' },
        excluiu: { tone: 'danger', label: 'Excluiu' },
        login: { tone: 'neutral', label: 'Login' },
        outro: { tone: 'neutral', label: 'Outro' },
    },
    // Próximos serviços/revisões recorrentes (`GET /vehicles/recurring-services/upcoming`,
    // `pages/service_order/UpcomingServices.js`) — status devolvidos pela API.
    recurringService: {
        vencido: { tone: 'danger', label: 'Vencido' },
        a_vencer: { tone: 'warning', label: 'A vencer' },
        em_dia: { tone: 'success', label: 'Em dia' },
    },
};

function toneToColors(theme, tone) {
    switch (tone) {
        case 'success':
            return { bg: theme.palette.success.bg, fg: theme.palette.success.main };
        case 'warning':
            return { bg: theme.palette.warning.bg, fg: theme.palette.warning.main };
        case 'danger':
            return { bg: theme.palette.error.bg, fg: theme.palette.error.main };
        case 'info':
            return { bg: theme.palette.info.bg, fg: theme.palette.info.main };
        case 'purple':
            return { bg: theme.palette.secondary.bg, fg: theme.palette.secondary.main };
        case 'brand2':
            return { bg: theme.palette.info.bg, fg: theme.palette.brand2.main };
        case 'neutral':
        default:
            return { bg: theme.palette.background.subtle, fg: theme.palette.text.disabled };
    }
}

/**
 * @param {'sale'|'orderService'|'serviceQuote'|'stockMovement'|'checklist'|'paymentMethod'|'itemType'|'connection'|'personType'|'auditAction'} domain
 * @param {string} value - raw value coming from the API (any case/accent)
 * @param {string} [label] - override the table's default label; used by domains
 *   like `auditAction` where `value` is only a color bucket and the real,
 *   human-readable text must come straight from the API instead of a fixed
 *   per-bucket label.
 * @param {'small'|'medium'} [size]
 * @param {object} [sx]
 */
export default function StatusChip({ domain, value, label, size = 'small', sx, ...rest }) {
    const theme = useTheme();
    const table = STATUS_TABLE[domain] || {};
    const entry = table[normalize(value)] || { tone: 'neutral', label: value ?? '—' };
    const { bg, fg } = toneToColors(theme, entry.tone);
    const Icon = entry.icon;

    return (
        <Chip
            size={size}
            label={label ?? entry.label}
            icon={Icon ? <Icon size={12} style={{ color: fg }} /> : undefined}
            sx={{
                backgroundColor: bg,
                color: fg,
                '& .MuiChip-icon': { color: fg, marginLeft: '8px' },
                ...(!Icon && {
                    '&::before': {
                        content: '""',
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: fg,
                        marginLeft: '10px',
                    },
                    '& .MuiChip-label': { pl: '6px' },
                }),
                ...sx,
            }}
            {...rest}
        />
    );
}
